import { io, Socket } from 'socket.io-client';
import type {
  ActiveCallPresence,
  AgentStatus,
  AgentStatusEntry,
  CallEventPayload,
  PresenceSnapshot,
  QueueSummary,
} from '@/types';
import { getClientConfig } from '@/lib/client-config';

// Socket event types (matching telephony-service/src/types)
// Mirrors telephony-service CampaignAgentConnectPayload.
export interface CampaignAgentConnectData {
  extension: string;
  queue: string;
  leadNumber: string;
  leadId: string | null;
  assigned: boolean;
  reason: string | null;
  linkedid: string | null;
  uniqueid: string | null;
}

interface ServerToClientEvents {
  'call:incoming': (data: CallEventPayload) => void;
  'campaign:agent-connect': (data: CampaignAgentConnectData) => void;
  'call:updated': (data: CallEventPayload) => void;
  'call:ended': (data: CallEventPayload) => void;
  'call:missed': (data: CallEventPayload) => void;
  'presence:update': (data: PresenceSnapshot) => void;
  'calls:active': (data: ActiveCallPresence[]) => void;
  'call:hold-changed': (data: { uniqueid: string; extension: string; held: boolean }) => void;
  'agent:status-changed': (data: { extension: string; status: AgentStatus; name: string }) => void;
  'agent:statuses': (data: AgentStatusEntry[]) => void;
  'queue:summary': (data: QueueSummary) => void;
}

interface ClientToServerEvents {
  'join:extension': (extension: string) => void;
  'leave:extension': (extension: string) => void;
  'join:supervisor': () => void;
  'leave:supervisor': () => void;
  'get:active-calls': () => void;
  'agent:set-status': (data: { extension: string; status: AgentStatus }) => void;
  'get:agent-statuses': () => void;
}

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

// Socket instance (singleton)
let socket: TypedSocket | null = null;

// Get or create socket connection
export function getTelephonySocket(): TypedSocket {
  if (!socket) {
    // Connect to the current origin so requests go through the nginx proxy
    // (/telephony-io/ → telephony:3002). Falls back to an explicit URL only if
    // NEXT_PUBLIC_TELEPHONY_SERVICE_URL is set (useful in local dev).
    const socketUrl = process.env.NEXT_PUBLIC_TELEPHONY_SERVICE_URL ||
      (typeof window !== 'undefined' ? window.location.origin : undefined);
    const serviceKey = process.env.NEXT_PUBLIC_TELEPHONY_SERVICE_KEY || '';

    socket = io(socketUrl as string, {
      path: '/telephony-io/',
      auth: {
        token: serviceKey,
      },
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
  }

  return socket;
}

// Connect to socket server. The auth token is resolved at runtime from
// /client-config (with the baked NEXT_PUBLIC_TELEPHONY_SERVICE_KEY as
// fallback) so the fleet-generic frontend image works on telephony clinics.
export function connectTelephonySocket(): void {
  const s = getTelephonySocket();
  if (!s.connected) {
    void getClientConfig().then(({ telephony_socket_token }) => {
      if (s.connected) return;
      (s.auth as { token: string }).token = telephony_socket_token || '';
      const resolvedUrl = process.env.NEXT_PUBLIC_TELEPHONY_SERVICE_URL ||
        (typeof window !== 'undefined' ? window.location.origin : 'unknown');
      console.log('[TelephonySocket] Initiating connection to', resolvedUrl);
      s.connect();
    });
  }
}

// Disconnect from socket server
export function disconnectTelephonySocket(): void {
  if (socket?.connected) {
    socket.disconnect();
  }
}

// Check if connected
export function isTelephonySocketConnected(): boolean {
  return socket?.connected ?? false;
}

// Join extension room
export function joinExtension(extension: string): void {
  const s = getTelephonySocket();
  if (s.connected) {
    console.log('[TelephonySocket] Joining extension:', extension);
    s.emit('join:extension', extension);
  }
}

// Leave extension room
export function leaveExtension(extension: string): void {
  const s = getTelephonySocket();
  if (s.connected) {
    s.emit('leave:extension', extension);
  }
}

// Join supervisor room
export function joinSupervisor(): void {
  const s = getTelephonySocket();
  if (s.connected) {
    s.emit('join:supervisor');
  }
}

// Leave supervisor room
export function leaveSupervisor(): void {
  const s = getTelephonySocket();
  if (s.connected) {
    s.emit('leave:supervisor');
  }
}

// Request active calls
export function requestActiveCalls(): void {
  const s = getTelephonySocket();
  if (s.connected) {
    s.emit('get:active-calls');
  }
}

// Event listener helpers
export function onCallIncoming(callback: (data: CallEventPayload) => void): () => void {
  const s = getTelephonySocket();
  s.on('call:incoming', callback);
  return () => s.off('call:incoming', callback);
}

export function onCallUpdated(callback: (data: CallEventPayload) => void): () => void {
  const s = getTelephonySocket();
  s.on('call:updated', callback);
  return () => s.off('call:updated', callback);
}

export function onCallEnded(callback: (data: CallEventPayload) => void): () => void {
  const s = getTelephonySocket();
  s.on('call:ended', callback);
  return () => s.off('call:ended', callback);
}

export function onPresenceUpdate(callback: (data: PresenceSnapshot) => void): () => void {
  const s = getTelephonySocket();
  s.on('presence:update', callback);
  return () => s.off('presence:update', callback);
}

export function onCallHoldChanged(callback: (data: { uniqueid: string; extension: string; held: boolean }) => void): () => void {
  const s = getTelephonySocket();
  s.on('call:hold-changed', callback);
  return () => s.off('call:hold-changed', callback);
}

// Fires only in the answering agent's extension room (sidecar AgentConnect on
// campaign_q*), after the backend matched the lead — older sidecar builds
// never emit it, so the CRM must stay correct without it.
export function onCampaignAgentConnect(callback: (data: CampaignAgentConnectData) => void): () => void {
  const s = getTelephonySocket();
  s.on('campaign:agent-connect', callback);
  return () => {
    s.off('campaign:agent-connect', callback);
  };
}

export function onCallMissed(callback: (data: CallEventPayload) => void): () => void {
  const s = getTelephonySocket();
  s.on('call:missed', callback);
  return () => s.off('call:missed', callback);
}

export function onActiveCalls(callback: (data: ActiveCallPresence[]) => void): () => void {
  const s = getTelephonySocket();
  s.on('calls:active', callback);
  return () => s.off('calls:active', callback);
}

// Connection event helpers
export function onTelephonyConnect(callback: () => void): () => void {
  const s = getTelephonySocket();
  s.on('connect', callback);
  return () => s.off('connect', callback);
}

export function onTelephonyDisconnect(callback: (reason: string) => void): () => void {
  const s = getTelephonySocket();
  s.on('disconnect', callback);
  return () => s.off('disconnect', callback);
}

export function onTelephonyConnectError(callback: (error: Error) => void): () => void {
  const s = getTelephonySocket();
  s.on('connect_error', callback);
  return () => s.off('connect_error', callback);
}

export function onAgentStatusChanged(callback: (data: { extension: string; status: AgentStatus; name: string }) => void): () => void {
  const s = getTelephonySocket();
  s.on('agent:status-changed', callback);
  return () => s.off('agent:status-changed', callback);
}

export function onAgentStatuses(callback: (data: AgentStatusEntry[]) => void): () => void {
  const s = getTelephonySocket();
  s.on('agent:statuses', callback);
  return () => s.off('agent:statuses', callback);
}

export function onQueueSummary(callback: (data: QueueSummary) => void): () => void {
  const s = getTelephonySocket();
  s.on('queue:summary', callback);
  return () => s.off('queue:summary', callback);
}

export function emitSetAgentStatus(extension: string, status: AgentStatus): void {
  const s = getTelephonySocket();
  if (s.connected) {
    s.emit('agent:set-status', { extension, status });
  }
}

export function requestAgentStatuses(): void {
  const s = getTelephonySocket();
  if (s.connected) {
    s.emit('get:agent-statuses');
  }
}
